import { type Patch, produce } from "immer";
import { v4 as uuidV4 } from "uuid";
import { type Actor, type SnapshotFrom, createActor, fromPromise, waitFor } from "xstate";

import { type CanvasSpecifier } from "../../api-types";
import { safeStringify } from "../../json";
import { type LoggerInterface } from "../../logger-types";
import { Transaction } from "../../metadata-client";
import { type DoTransactionFn } from "../../metadata-state";
import {
	ERROR_NO_ACTIVE_TRANSACTION,
	type SubmitTransactionInput,
	type SubmitTransactionOutput,
	makeStateSyncClientMachine,
	waitForTransactionProcessed,
} from "../../state-sync-client";
import { PageLocalStorageApiClient } from "../api-types";
import { type LocalStorage } from "../server";

export class PageLocalStorageClient {
	/** Our link to the metadata server, usually hosted as a Cloudflare Durable Object. */
	private canvasSpecifier: CanvasSpecifier;
	private pageLocalStorageApiClient: PageLocalStorageApiClient;
	private logger: LoggerInterface;

	_clientMachine: Actor<ReturnType<typeof makeStateSyncClientMachine<LocalStorage>>>;
	private inFlightPingIDs = new Set<string>();
	private _pingInterval?: ReturnType<typeof setInterval>;

	private _offlineHandler = () => {
		this._clientMachine.send({ type: "connectionFailureDetected" });
	};

	private _onlineHandler = () => {
		this._clientMachine.send({ type: "connectionRestored" });
	};

	constructor(opts: {
		canvasSpecifier: CanvasSpecifier;
		fetch: (init?: RequestInit | undefined) => Promise<Response>;

		logger: LoggerInterface;
		pingIntervalMs?: number;
	}) {
		this.pageLocalStorageApiClient = new PageLocalStorageApiClient(opts.fetch);
		this.logger = opts.logger;
		this.canvasSpecifier = opts.canvasSpecifier;

		this._clientMachine = createActor(
			makeStateSyncClientMachine<LocalStorage>({
				connect: fromPromise(async ({ input }) => {
					const response = await this.pageLocalStorageApiClient.initialize(
						input.extra?.["canvasSpecifier"]
					);
					if (response.type === "success") {
						return { version: response.version, data: response.localStorage };
					} else {
						this.logger.error("unable to connect", {
							response: safeStringify(response),
						});
						throw new Error("unable to connect");
					}
				}),
				submitTransaction: fromPromise<
					SubmitTransactionOutput,
					SubmitTransactionInput<LocalStorage>
				>(async ({ input: activeTransaction }) => {
					if (!activeTransaction) {
						throw ERROR_NO_ACTIVE_TRANSACTION;
					}

					const transactionId = activeTransaction.id ?? uuidV4();
					return await this.pageLocalStorageApiClient.enqueueTransaction(
						transactionId,
						activeTransaction.patches,
						activeTransaction.lastVersionSeen
					);
				}),
				fetchMissingTransactions: fromPromise<unknown, { version: number }>(async () => {
					this.logger.debug("fetch missing transactions not supported");
					return;
				}),
			})
		);
		this._clientMachine.start();

		// FIXME: We do not know how to detect that the Partysocket has dropped, so we just check
		// whether the connection is offline generally.
		window.addEventListener("offline", this._offlineHandler);
		window.addEventListener("online", this._onlineHandler);

		if (opts.pingIntervalMs) {
			this._pingInterval = setInterval(() => {}, opts.pingIntervalMs);
		}
	}

	/** Initializes the canvas room. */
	async idempotentInitRoom(): ReturnType<
		typeof waitFor<PageLocalStorageClient["_clientMachine"]>
	> {
		const canvasSpecifier = this.canvasSpecifier;
		const waiter = waitFor(this._clientMachine, (snap) => snap.matches("connected"));
		this._clientMachine.send({ type: "connect", extra: { canvasSpecifier } });

		const snap = await waiter;
		return snap;
	}

	makeTransaction(doTransaction: DoTransactionFn<LocalStorage>): Transaction<LocalStorage> {
		const patches: Patch[] = [];
		const inversePatches: Patch[] = [];
		const snap = this._clientMachine.getSnapshot();
		const newState = produce<LocalStorage>(
			snap.context.localData.data,
			doTransaction,
			(_patches, _inversePatches) => {
				patches.push(..._patches);
				inversePatches.push(..._inversePatches);
			}
		);

		return new Transaction(newState, patches, inversePatches);
	}

	async commit(): ReturnType<typeof waitForTransactionProcessed<Record<string, unknown>>> {
		const transactionId = uuidV4();
		const snap = this._clientMachine.getSnapshot();
		if (!snap.matches({ connected: { editableStatus: "enabled" } })) {
			throw new Error(
				`state machine is not accepting transactions. current machine state: ${snap.value}`
			);
		}

		return await waitForTransactionProcessed(this._clientMachine, transactionId);
	}

	acknowledgedPong(uuid: string) {
		if (this.inFlightPingIDs.has(uuid)) {
			this.inFlightPingIDs.delete(uuid);
		} else {
			this.logger.debug("attempted to acknowledge a non-existent uuid from a pong", {
				unknownID: uuid,
				inFlightPingIDs: this.inFlightPingIDs,
			});
		}
	}

	subscribe<U>(onUpdate: (t: LocalStorage, snap: SnapshotFrom<typeof this._clientMachine>) => U) {
		// TODO: This hook fires on every state transition, which is what we want, but there's also
		// no way (outside React hooks) to memoize it. We should probably allow for this case.
		return this._clientMachine.subscribe((snap) => onUpdate(snap.context.localData.data, snap));
	}

	dispose() {
		clearInterval(this._pingInterval);
		window.removeEventListener("offline", this._offlineHandler);
		window.removeEventListener("online", this._onlineHandler);
		this._clientMachine.stop();
		// close the room client (aka the socket) on dispose
		// the room client isn't reused across different client instances.
	}
}
